import { useState } from 'react';
import { useCanAccess, useNotify, useRecordContext, useRefresh } from 'ra-core';
import {
  anonymiserUnCompte,
  retirerUnRattachement,
  fermerUneRelation,
} from '@groupe-trousseau/shared/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Confirm } from '@/components/admin/confirm';
import { deballer, lireLErreur } from '../../fournisseurs/client';

type Rattachement = {
  id: string;
  structureId: string;
  structureNom: string;
  identifiantExterne: string | null;
  debutLe: string;
  finLe: string | null;
};
type Relation = {
  id: string;
  tuteurPrenom: string | null;
  elevePrenom: string | null;
  creeeParStructureId: string;
  fermeeLe: string | null;
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LES GESTES SUR UN COMPTE — ET AUCUN NE PASSE PAR UN FORMULAIRE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `DOCTRINE.md` §7 : « un admin ne fait pas que du CRUD. Il porte des **actions métier** qui
 * enchaînent plusieurs opérations. […] Dans React Admin, une action métier custom est **du
 * React ordinaire** : un bouton, un appel au client d'API, une invalidation de cache. **On ne
 * cherche jamais à faire entrer une action complexe dans un formulaire de ressource.** »
 *
 * **Trois gestes, et trois routes qui vivent ailleurs.** D-071 et le second amendement de
 * `CDC-5` §7 : ce document déclarait ses propres routes pour deux d'entre eux, et
 * **`CDC-1` les portait déjà, avec les mêmes rôles et les mêmes bornes**.
 *
 * | Geste | Route | Pourquoi pas une route de `CDC-5` |
 * |---|---|---|
 * | retirer un rattachement | `DELETE /provisionnement/rattachements/:id` | livrée au LOT-1, `orgadmin` et `superadmin` |
 * | défaire une relation | `DELETE /provisionnement/relations/:id` | idem, **et la borne fine y était déjà** |
 * | anonymiser | `POST /comptes/:id/anonymisation` | le seul geste destructif du produit |
 *
 * D-071, sur la deuxième ligne : « la borne la plus fine de ce document **était déjà tenue** :
 * "`orgadmin` **les siennes seulement**" vit dans `provisioning.service.ts` depuis le LOT-1, et
 * **elle y lève le code exact que ce document nomme**, `RELATION_NON_CREEE_PAR_CETTE_STRUCTURE`.
 * Écrire une seconde route aurait donc demandé de **réécrire une borne déjà juste, à côté
 * d'elle**. »
 *
 * **Conséquence pour cet écran** : il montre le bouton « défaire » sur **toutes** les
 * relations, et **laisse le serveur trancher l'objet**. Filtrer ici sur
 * `creeeParStructureId` recopierait la borne — troisième copie —, et c'est celle qui
 * divergerait.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **RETIRER UN RATTACHEMENT NE SUPPRIME RIEN, ET SA CONSÉQUENCE EST CONDITIONNELLE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-60 : « **retirer un rattachement laisse la ligne en place avec sa date de fin.** » Et :
 * « **retirer un rattachement ne ferme une relation que si c'est le DERNIER de la personne** »
 * (Q542). F-68 en fait un critère : « QUAND le **dernier** rattachement d'une personne est
 * retiré, LE SYSTÈME DOIT fermer ses relations **et envoyer l'email de perte d'accès** — **et
 * pas avant**. »
 *
 * **C'est pourquoi l'ordre des gestes d'extinction est écrit** (F-68) : « les relations créées
 * par une structure **ne tombent PAS toutes seules** […] **d'où l'ordre** » — défaire les
 * relations d'abord, vider les colonnes, retirer les rattachements, révoquer le secret en
 * dernier. La confirmation le dit, parce que rien dans le produit ne le rappellera.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **L'ANONYMISATION EST LE SEUL GESTE DESTRUCTIF DU PRODUIT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-61 : « il crée, modifie et **anonymise** un compte, sur toutes les structures, chaque
 * action tracée. » Et l'avertissement que la confirmation doit porter : « **l'anonymisation
 * n'est pas un geste réservé aux tuteurs et aux élèves** : anonymiser la personne qui tenait un
 * espace **fait tomber son rattachement, et la structure se retrouve sans personne pour la
 * tenir** » — cas de bord 3, qui renvoie au cas 2.
 *
 * F-62 en donne l'autre moitié, qui rassure sur ce que le geste **ne** détruit pas : « **la
 * somme des minutes compte les cours des comptes anonymisés, et elle ne baisse jamais
 * rétroactivement.** Le participant apparaît sous une **désignation anonyme neutre, sans cause
 * et sans date**. »
 */
export function GestesSurUnCompte() {
  const compte = useRecordContext<{
    id: string;
    role: string;
    etatDuCompte: string;
    rattachements: Rattachement[];
    relations: Relation[];
  }>();
  const notifier = useNotify();
  const rafraichir = useRefresh();

  const { canAccess: peutAnonymiser } = useCanAccess({
    resource: 'comptes',
    action: 'anonymiser',
  });
  const { canAccess: peutRetirerUnRattachement } = useCanAccess({
    resource: 'rattachements',
    action: 'delete',
  });
  const { canAccess: peutDefaireUneRelation } = useCanAccess({
    resource: 'relations',
    action: 'defaire-une-relation',
  });

  /**
   * **L'ANONYMISATION DEMANDE LE MOT DE PASSE DE QUI L'EXÉCUTE, ET C'EST LA ROUTE QUI
   * L'EXIGE.**
   *
   * `POST /api/v1/comptes/:id/anonymisation` porte `motDePasseCourant` en champ **requis**.
   * Ce n'est pas une précaution d'écran : c'est une preuve de présence sur le seul geste
   * irréversible du produit, et le contrat la réclame — un formulaire qui ne la demanderait
   * pas produirait un `400` sur un bouton qui a l'air prêt.
   *
   * **Il est tenu en état local et jamais ailleurs** : ni dans un magasin, ni dans le cache
   * de `ra-core`, ni dans l'adresse. Il est vidé après chaque tentative, réussie ou non.
   */
  const [motDePasse, setMotDePasse] = useState('');
  const [aConfirmer, setAConfirmer] = useState<
    | { readonly quoi: 'anonymisation' }
    | { readonly quoi: 'rattachement'; readonly id: string; readonly dernier: boolean }
    | { readonly quoi: 'relation'; readonly id: string }
    | null
  >(null);

  if (compte === undefined) return null;

  const rattachementsActifs = compte.rattachements.filter((r) => r.finLe === null);
  const relationsOuvertes = compte.relations.filter((r) => r.fermeeLe === null);

  const agir = async (action: () => Promise<unknown>, message: string) => {
    try {
      await action();
      notifier(message, { type: 'info' });
      rafraichir();
    } catch (erreur) {
      const lue = lireLErreur(erreur);
      notifier(
        lue.code === 'RELATION_NON_CREEE_PAR_CETTE_STRUCTURE'
          ? 'Cette relation n’a pas été créée par votre structure : vous ne défaites que les ' +
              'vôtres. Le compte qui voit tout les défait toutes.'
          : lue.message,
        { type: 'error' },
      );
    } finally {
      setAConfirmer(null);
      setMotDePasse('');
    }
  };

  return (
    <section className="flex flex-col gap-5">
      {/* ─── LES RATTACHEMENTS ─────────────────────────────────────────────── */}
      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-medium">Rattachements</h3>
        {compte.rattachements.length === 0 ? (
          <p className="text-muted-foreground text-sm">Aucun rattachement.</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {compte.rattachements.map((rattachement) => (
              <li key={rattachement.id} className="flex items-center gap-3">
                <span className="font-medium">{rattachement.structureNom}</span>
                <span className="text-muted-foreground text-xs">
                  depuis le {new Date(rattachement.debutLe).toLocaleDateString('fr-FR')}
                  {rattachement.finLe !== null &&
                    ` — retiré le ${new Date(rattachement.finLe).toLocaleDateString('fr-FR')}`}
                </span>
                {/* **La ligne retirée reste affichée avec sa date de fin**, F-60 : « retirer un
                    rattachement laisse la ligne en place avec sa date de fin ». La masquer
                    ferait disparaître l'historique que F-68 exige de rendre — critère 3 :
                    « ses rattachements retirés **avec leurs dates de fin** ». */}
                {rattachement.finLe === null && peutRetirerUnRattachement && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setAConfirmer({
                        quoi: 'rattachement',
                        id: rattachement.id,
                        dernier: rattachementsActifs.length === 1,
                      })
                    }
                  >
                    Retirer
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ─── LES RELATIONS ─────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-medium">Relations</h3>
        <p className="text-muted-foreground text-xs">
          Les relations extérieures sont incluses, et c’est assumé : elles appartiennent à la
          personne, pas à la structure qui la lit.
        </p>
        {relationsOuvertes.length === 0 ? (
          <p className="text-muted-foreground text-sm">Aucune relation ouverte.</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {relationsOuvertes.map((relation) => (
              <li key={relation.id} className="flex items-center gap-3">
                <span>
                  {relation.tuteurPrenom ?? '—'} ↔ {relation.elevePrenom ?? '—'}
                </span>
                {peutDefaireUneRelation && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAConfirmer({ quoi: 'relation', id: relation.id })}
                  >
                    Défaire
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ─── L'ANONYMISATION ───────────────────────────────────────────────── */}
      {peutAnonymiser && compte.etatDuCompte !== 'ANONYMIZED' && (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-medium">Anonymisation</h3>
          <Button
            variant="destructive"
            className="self-start"
            onClick={() => setAConfirmer({ quoi: 'anonymisation' })}
          >
            Anonymiser ce compte
          </Button>
        </div>
      )}

      <Confirm
        isOpen={aConfirmer?.quoi === 'rattachement'}
        title="Retirer ce rattachement ?"
        content={
          aConfirmer?.quoi === 'rattachement' && aConfirmer.dernier
            ? 'C’est le DERNIER rattachement actif de cette personne : ses relations seront ' +
              'fermées et l’email de perte d’accès partira. La ligne reste en place avec sa ' +
              'date de fin.'
            : 'La ligne reste en place avec sa date de fin. Les relations de la personne ne ' +
              'sont PAS fermées : elles ne le sont qu’au retrait de son dernier rattachement. ' +
              'Pour éteindre une structure, défaites d’abord ses relations — c’est l’ordre ' +
              'écrit en F-68.'
        }
        onConfirm={() => {
          if (aConfirmer?.quoi !== 'rattachement') return;
          void agir(
            () => deballer(retirerUnRattachement({ path: { id: aConfirmer.id } })),
            'Rattachement retiré. Sa ligne porte désormais une date de fin.',
          );
        }}
        onClose={() => setAConfirmer(null)}
      />

      <Confirm
        isOpen={aConfirmer?.quoi === 'relation'}
        title="Défaire cette relation ?"
        content={
          'Vous ne défaites que les relations créées par votre structure ; le compte qui voit ' +
          'tout les défait toutes. Défaire une relation est le PREMIER geste de l’extinction ' +
          'd’une structure, avant de retirer les rattachements.'
        }
        onConfirm={() => {
          if (aConfirmer?.quoi !== 'relation') return;
          void agir(
            () => deballer(fermerUneRelation({ path: { id: aConfirmer.id } })),
            'Relation défaite.',
          );
        }}
        onClose={() => setAConfirmer(null)}
      />

      <Confirm
        isOpen={aConfirmer?.quoi === 'anonymisation'}
        title="Anonymiser ce compte ? C’est irréversible."
        content={
          <div className="flex flex-col gap-3">
            <p>
              C’est le seul geste destructif du produit, et il ne se défait pas. Si cette
              personne tenait l’espace d’une structure, son rattachement tombe et la structure
              se retrouve sans personne pour la tenir — seul le compte qui voit tout pourra lui
              en désigner une autre. La somme des minutes ne baisse pas : les cours restent
              comptés, sous une désignation anonyme neutre.
            </p>
            <div className="flex flex-col gap-1">
              <Label htmlFor="motDePasseCourant">Votre mot de passe</Label>
              <Input
                id="motDePasseCourant"
                type="password"
                autoComplete="current-password"
                value={motDePasse}
                onChange={(e) => setMotDePasse(e.target.value)}
              />
            </div>
          </div>
        }
        onConfirm={() => {
          if (motDePasse === '') {
            notifier('Le mot de passe est requis pour anonymiser un compte.', {
              type: 'warning',
            });
            return;
          }
          void agir(
            () =>
              deballer(
                anonymiserUnCompte({
                  path: { id: compte.id },
                  body: { motDePasseCourant: motDePasse },
                }),
              ),
            'Compte anonymisé. Le geste est tracé.',
          );
        }}
        onClose={() => {
          setAConfirmer(null);
          setMotDePasse('');
        }}
      />
    </section>
  );
}
